import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useWorkspaces, Workspace } from 'zens-store'
import { Color, Icon, Radius, Spacing, Text } from 'zens-storybook'
import { useTranslation } from 'react-i18next'

import { FloorplanView, FloorplanZoomView, FloorplanZoomViewProps } from '../floorplan'
import { RefreshInfo } from '../refresh'

import CareLegend from './legend'
import { careStatusColors, colorForCareWorkspaceStatus } from './statuscolor'

const styles = StyleSheet.create({
  container: { flexGrow: 1 },
  floorplan: { flexGrow: 1, flexBasis: 240, minHeight: 240 },
  refresh: { alignSelf: 'flex-end', margin: Spacing.S },
  empty: { padding: Spacing.M },
  details: {
    position: 'absolute',
    top: Spacing.S,
    right: Spacing.S,
    width: 300,
    maxWidth: '90%',
    padding: Spacing.S,
    paddingRight: Spacing.L + Spacing.S,
    backgroundColor: Color.simplyWhite,
    borderColor: Color.calmGrey,
    borderWidth: 1,
    borderRadius: Radius.S,
  },
  detailsLabel: { marginTop: Spacing.XS, color: Color.metalGrey },
  close: { position: 'absolute', top: Spacing.XS, right: Spacing.XS, padding: Spacing.XS },
})

interface Props extends FloorplanZoomViewProps {
  companyId: number
  floorId: number
  zoneId?: number
}

const CareFloorplan = (props: Props) => {
  const { companyId, floorId, zoneId = 0 } = props
  const selection = useMemo(() => ({ companyId, floorId, zoneId }), [companyId, floorId, zoneId])
  const deferredSelection = useDeferredValue(selection)
  // Keep the current colors visible while the next zone loads, but never reuse another floor's selection.
  const displayedZoneId =
    deferredSelection.companyId === companyId && deferredSelection.floorId === floorId
      ? deferredSelection.zoneId
      : zoneId

  return <CareFloorplanContent {...props} zoneId={displayedZoneId} zoneLoading={displayedZoneId !== zoneId} />
}

const CareFloorplanContent = ({
  companyId,
  floorId,
  zoneId = 0,
  zoneLoading,
  style,
  ...props
}: Props & { zoneLoading: boolean }) => {
  const { t } = useTranslation()
  const workspaces = useWorkspaces(
    { companyId, filter: { floorId: [floorId] } },
    { enabled: !!floorId, refetchInterval: 60000 },
  )
  const zoneWorkspaces = useWorkspaces(
    { companyId, filter: { floorId: [floorId], zoneId: [zoneId] } },
    { enabled: !!floorId && !!zoneId, refetchInterval: 60000 },
  )
  const [selected, setSelected] = useState<{ companyId: number; floorId: number; workspaceId: number }>()
  useEffect(() => {
    setSelected(undefined)
  }, [companyId, floorId])
  const selectedWorkspace =
    selected?.companyId === companyId && selected.floorId === floorId
      ? workspaces.data?.data.find((workspace) => workspace.id === selected.workspaceId)
      : undefined
  const zoneIds = new Set(zoneWorkspaces.data?.data.map((workspace) => workspace.id))
  const outsideZone = (workspace: Workspace) => !!zoneId && !zoneIds.has(workspace.id)

  if (!floorId) return <Text style={styles.empty}>{t('No floor available')}</Text>

  return (
    <View style={styles.container}>
      <RefreshInfo
        date={workspaces.dataUpdatedAt}
        onRefresh={() => {
          workspaces.refetch()
          if (zoneId) zoneWorkspaces.refetch()
        }}
        loading={zoneLoading || workspaces.isFetching || (!!zoneId && zoneWorkspaces.isFetching)}
        style={styles.refresh}
      />
      <FloorplanZoomView
        {...props}
        style={[styles.floorplan, style]}
        overlay={
          selectedWorkspace ? (
            <View style={styles.details} testID="CareFloorplan.WorkspaceDetails">
              <Text level={7} style={styles.detailsLabel}>
                {t('Workspace name')}
              </Text>
              <Text>{selectedWorkspace.name || '—'}</Text>
              <Text level={7} style={styles.detailsLabel}>
                {t('Device serial number')}
              </Text>
              <Text>
                {selectedWorkspace.product ? selectedWorkspace.product.serialNumber || '—' : t('No device assigned')}
              </Text>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={t('Close')}
                style={styles.close}
                onPress={() => setSelected(undefined)}
                testID="CareFloorplan.CloseDetails"
              >
                <Icon name="close" size="S" color={Color.premiumBlack} />
              </TouchableOpacity>
            </View>
          ) : undefined
        }
      >
        <FloorplanView
          companyId={companyId}
          floorId={floorId}
          data={workspaces.data?.data}
          onItemPress={(item) => setSelected({ companyId, floorId, workspaceId: item.id })}
          onBackgroundPress={() => setSelected(undefined)}
          itemColor={(item) =>
            outsideZone(item as Workspace)
              ? careStatusColors.outsideZone
              : colorForCareWorkspaceStatus(item as Workspace)
          }
          itemBorderColor={(item) =>
            item.id === selectedWorkspace?.id
              ? Color.blue
              : outsideZone(item as Workspace)
                ? Color.calmGrey
                : Color.premiumBlack
          }
        />
      </FloorplanZoomView>
      <CareLegend zoneSelected={!!zoneId} />
    </View>
  )
}

export default CareFloorplan
